import { _tryFetch } from '@/lib/bouqsFetch';
import {
  PagesContentAreas,
  ContentAreas,
  HomePageResponse,
  CmsPageResponse,
} from '@/interfaces';
import { Navigation } from '@/components/TopNav/TopNav.d';
import { DotwData } from '@/components/Dotw/Dotw.d';
import { GetDotwPayload, QueryParams, UrlPath, Slug } from './content.d';
import { getValidQs, allowlistedQueryParams } from '@/utils/urlQueryParams';
import { cmsPageResponse } from '../staticPageHelper';
import { cmsFlowerCareInstructionsPageResponse } from '../staticsPages/FlowerCareInstructions';
import { cmsSiteMapPageResponse } from '../staticsPages/SiteMap';
import { cmsDealOfTheWeekPageResponse } from '../staticsPages/DealOfTheweek';
import { cmsVideoPageResponse } from '../staticsPages/BouqsVideo';
import { cmsOurDifferencePageResponse } from '../staticsPages/OurDifferences';

// Leaving second param as optional and TS auto types optional params
function _generateFetchUrl(slug: Slug, qs = '') {
  return `/${slug}?${qs}`;
}

export async function fetchHomePage(
  qs: QueryParams
): Promise<HomePageResponse> {
  const allowlistedQs = getValidQs(qs, allowlistedQueryParams.marketing);

  const fetchUrl = _generateFetchUrl('contents/', allowlistedQs);
  return await _tryFetch(fetchUrl);
}

export async function fetchCmsPage(slug: string): Promise<CmsPageResponse> {
  switch (slug) {
    case 'flower-care-instructions':
      return cmsFlowerCareInstructionsPageResponse;
    case 'sitemap':
      return cmsSiteMapPageResponse;
    case 'deal-of-the-week':
      return cmsDealOfTheWeekPageResponse;
    case 'video':
      return cmsVideoPageResponse;
    case 'our-difference':
      return cmsOurDifferencePageResponse;
    default:
      return cmsPageResponse;
  }
}

export async function fetchHeaderPromo(
  currentPath: UrlPath,
  qs = ''
): Promise<ContentAreas> {
  let allowlistedQs = getValidQs(qs, allowlistedQueryParams.marketing);

  /* harcoding page_path since it is only required for this specific endpoint */
  allowlistedQs = allowlistedQs.length
    ? `${allowlistedQs}&page_path=${currentPath}`
    : `page_path=${currentPath}`;
  const fetchUrl = _generateFetchUrl('header_promo', `${allowlistedQs}`);
  return await _tryFetch(fetchUrl);
}

export async function fetchStaticContent(
  qs: QueryParams
): Promise<PagesContentAreas> {
  const allowlistedQs = getValidQs(qs, allowlistedQueryParams.marketing);
  const fetchUrl = _generateFetchUrl('layout_content', allowlistedQs);
  return await _tryFetch(fetchUrl);
}

export async function getTopNav(): Promise<
  Navigation | Record<string, string>
> {
  const fetchUrl: string = _generateFetchUrl('navigation_menus/top_nav');
  const res = await _tryFetch(fetchUrl);
  // TODO: think about what other possible response is acceptable besides an empty object if the response is falsy
  return res?.navigation ?? {};
}

export async function getDotw(): Promise<DotwData> {
  const fetchUrl: string = _generateFetchUrl('deals_of_the_week', '');
  const res: GetDotwPayload | undefined = await _tryFetch(fetchUrl);

  return (
    res && {
      id: res.id,
      active: res.active,
      display: res.display,
      availability: res.product_availability,
      price: res.sell_price,
      discountedPrice: res.discounted_price,
      discount: res.discount,
      url: res.storefront_url,
      size: res.size,
      delivery: res.date,
      occasion: res.order_item_preset?.occasion,
      availableBy: res.order_item_preset?.soonest_available_date,
      vase: res.order_item_preset?.vase,
      name: res.product.name,
      description: res.product.short_description,
      imageAlt: res.product.img_alt,
      imageUrl: res.product.primary_image_url,
      sku: res.order_item_preset?.sku,
      productId: res.product?.id,
      availableHeading: res?.available_heading,
      availableDescription: res?.available_description,
      dotwCta: res?.cta,
    }
  );
}
